import * as stylex from "@stylexjs/stylex";
import { colors, fonts, fontSizes } from "@softmax/design-system/tokens.stylex";
import { type ReactNode } from "react";

import { modesValue } from "./store-mode-label";

export type StoreDetailRow = {
  readonly key: string;
  readonly label: string;
  readonly value: ReactNode;
};

type StoreDetailsData = {
  readonly developer: {
    readonly name: string;
    readonly href: string | null;
  } | null;
  readonly publisher: {
    readonly name: string;
    readonly href: string | null;
  } | null;
  readonly released: { readonly at: string; readonly label: string } | null;
  readonly leagueOpenedAt: string | null;
  readonly version: string | null;
  readonly modes: readonly string[];
  readonly tags: readonly string[];
};

function dateOf(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(iso));
}

export function storeDetailRows(details: StoreDetailsData): StoreDetailRow[] {
  const rows: StoreDetailRow[] = [];

  if (details.developer)
    rows.push({
      key: "developer",
      label: "Developer",
      value: details.developer.href ? (
        <a
          href={details.developer.href}
          target="_blank"
          rel="noopener noreferrer"
          {...stylex.props(styles.value)}
        >
          {details.developer.name}
        </a>
      ) : (
        details.developer.name
      ),
    });

  if (details.publisher)
    rows.push({
      key: "publisher",
      label: "Publisher",
      value: details.publisher.name,
    });

  if (details.released)
    rows.push({
      key: "released",
      label: details.released.label,
      value: dateOf(details.released.at),
    });

  if (details.leagueOpenedAt)
    rows.push({
      key: "opened",
      label: "League opened",
      value: dateOf(details.leagueOpenedAt),
    });

  if (details.version)
    rows.push({ key: "version", label: "Version", value: details.version });

  if (details.modes.length)
    rows.push({
      key: "modes",
      label: "Modes",
      value: modesValue(details.modes),
    });

  return rows;
}

const styles = stylex.create({
  value: {
    margin: 0,
    fontFamily: fonts.mono,
    fontSize: fontSizes.small,
    lineHeight: "18px",
    color: colors.foreground,
  },
});
